import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getK3Agents, setK3Tasks, getK3Tasks, assignAgentJob, getK3TaskLogs, getK3Scripts, getCommits } from '../../actions';
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Table,
  Badge,
  Spinner,
  Modal,
  OverlayTrigger,
  Tooltip,
  Select,
  Toast,
  ToastContainer
} from 'react-bootstrap';
import { BsPencil, BsPlus, BsCircleFill, BsList } from 'react-icons/bs';
import K3TaskView from './K3TaskView';
import K3TaskLogModal from '../../components/K3/K3TaskLogModal';
import { v4 as uuidv4 } from 'uuid';
import K3TaskCreateModal from './K3TaskCreateModal';

class K3TaskPlayground extends Component {
  constructor(props) {
    super(props);
    this.state = {
      formData: {
        type: '',
        name: '',
        description: '',
        content: '',
        task_report_id: '',
        status: 'pending',
        priority: 'medium',
        src_agent_id: '',
        target_agent_id: '',
        script_id: '',
        parent_task_id: '',
        children_task_ids: [],
        tags: '',
        input_data: '{}',
        output_data: '{}',
        error_message: '',
        metadata: '{}'
      },
      k3agents: [],
      k3tasks: [],
      k3scripts: [],
      loading: false,
      selectedTask: null,
      showTaskView: false,
      showCreateModal: false,
      selectedJobs: {},
      showLogModal: false,
      selectedTaskId: null,
      taskLogs: [],
      showLatestLogsFirst: true,
      showSystemInfo: true,
      toast: {
        show: false,
        message: '',
        type: 'success' // or 'danger' for errors
      },
      filters: {
        type: '',
        status: '',
        priority: ''
      },
      commits: []
    };
    this.refreshInterval = null;
  }

  componentDidMount() {
    this.props.getK3Agents();
    this.props.getK3Tasks();
    this.props.getK3Scripts();
    this.props.getCommits();
    
    // Set up auto-refresh every 2 minutes
    this.refreshInterval = setInterval(() => {
      this.props.getK3Tasks();
    }, 2 * 60 * 1000); // 2 minutes in milliseconds
  }

  componentWillUnmount() {
    // Clear the interval when component unmounts
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { k3tasks, k3agents, k3scripts, commits } = nextProps;
    
    if (k3tasks) {
      this.setState({ k3tasks: k3tasks });
    }

    if (k3agents) {
      this.setState({ k3agents: k3agents });
    }

    if (k3scripts) {
      this.setState({ k3scripts: k3scripts });
    }

    if (commits) {
      this.setState({ commits: commits });
    }
  }

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [name]: value
      }
    }));
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ loading: true });

    try {
      const taskData = {
        type: this.state.formData.type,
        name: this.state.formData.name,
        description: this.state.formData.description,
        content: this.state.formData.content,
        task_report_id: this.state.formData.task_report_id,
        status: this.state.formData.status,
        priority: this.state.formData.priority,
        src_agent_id: this.state.formData.src_agent_id,
        target_agent_id: this.state.formData.target_agent_id,
        script_id: this.state.formData.script_id,
        parent_task_id: this.state.formData.parent_task_id,
        children_task_ids: this.state.formData.children_task_ids,
        tags: this.state.formData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        input_data: JSON.parse(this.state.formData.input_data),
        output_data: JSON.parse(this.state.formData.output_data),
        error_message: this.state.formData.error_message,
        metadata: JSON.parse(this.state.formData.metadata)
      };

      await this.props.setK3Tasks([taskData]);
      await this.props.getK3Tasks();

      // Show success toast and close modal
      this.setState({
        toast: {
          show: true,
          message: 'Task created successfully!',
          type: 'success'
        },
        showCreateModal: false,
        // Reset form
        formData: {
          type: '',
          name: '',
          description: '',
          content: '',
          task_report_id: '',
          status: 'pending',
          priority: 'medium',
          src_agent_id: '',
          target_agent_id: '',
          script_id: '',
          parent_task_id: '',
          children_task_ids: [],
          tags: '',
          input_data: '{}',
          output_data: '{}',
          error_message: '',
          metadata: '{}'
        }
      });
    } catch (error) {
      console.error('Error creating task:', error);
      // Show error toast but keep modal open
      this.setState({
        toast: {
          show: true,
          message: 'Error creating task: ' + (error.message || 'Unknown error'),
          type: 'danger'
        }
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  getStatusBadge = (status) => {
    const variants = {
      pending: 'secondary',
      in_progress: 'primary',
      completed: 'success',
      failed: 'danger'
    };
    return <Badge bg={variants[status]}>{status}</Badge>;
  };

  getPriorityBadge = (priority) => {
    const variants = {
      low: 'info',
      medium: 'warning',
      high: 'danger',
      urgent: 'danger'
    };
    return <Badge bg={variants[priority]}>{priority}</Badge>;
  };

  getAgentName = (agentId) => {
    const agent = this.props.k3agents.find(a => a._id === agentId);
    return agent ? `${agent.name}` : agentId;
  };

  isChatGPTWebBot = (agentId) => {
    const agent = this.props.k3agents.find(a => a._id === agentId);
    return agent && agent.main_type === 'AI' && agent.sub_type === 'AI_CHAT_GPT';
  };

  isPythonScriptRunner = (agentId) => {
    const agent = this.props.k3agents.find(a => a._id === agentId);
    return agent && agent.main_type === 'MACHINE' && agent.sub_type === 'SCRIPT_RUNNER_PYTHON';
  };

  isHelperMarkItDown = (agentId) => {
    const agent = this.props.k3agents.find(a => a._id === agentId);
    return agent && agent.main_type === 'MACHINE' && agent.sub_type === 'HELPER_MARKITDOWN';
  };

  handleEditTask = (task) => {
    this.setState({
      selectedTask: task,
      showTaskView: true
    });
  };

  handleCloseTaskView = () => {
    this.setState({
      selectedTask: null,
      showTaskView: false
    });
  };

  handleJobChange = (taskId, e) => {
    const new_job_id = e.target.value ? parseInt(e.target.value) : null;
    this.setState(prevState => ({
      selectedJobs: {
        ...prevState.selectedJobs,
        [taskId]: {
          job_id: new_job_id,
          content: {}
        }
      }
    }));
  };

  handleJobContentChange = (taskId, jobId, e) => {
    const { name, value } = e.target;
    console.log('handleJobContentChange', jobId, name, value);
    this.setState(prevState => {
      const currentJobData = prevState.selectedJobs[taskId] || { job_id: null, content: {} };
      return {
        selectedJobs: {
          ...prevState.selectedJobs,
          [taskId]: {
            ...currentJobData,
            content: {
              ...currentJobData.content,
              [name]: value
            }
          }
        }
      };
    });
  };

  handleCloseCreateModal = () => {
    this.setState({
      showCreateModal: false,
      formData: {
        type: '',
        name: '',
        description: '',
        content: '',
        task_report_id: '',
        status: 'pending',
        priority: 'medium',
        src_agent_id: '',
        target_agent_id: '',
        script_id: '',
        parent_task_id: '',
        children_task_ids: [],
        tags: '',
        input_data: '{}',
        output_data: '{}',
        error_message: '',
        metadata: '{}'
      }
    });
  };

  handleShowCreateModal = () => {
    this.setState({ showCreateModal: true });
  };

  getAgentStatus = (agentId) => {
    const agent = this.props.k3agents.find(a => a._id === agentId);
    if (!agent || !agent.last_heartbeat) return null;

    const lastHeartbeat = new Date(agent.last_heartbeat);
    const now = new Date();
    const minutesDiff = Math.floor((now - lastHeartbeat) / (1000 * 60));
    const isOnline = minutesDiff <= 5;

    return {
      isOnline,
      minutesDiff,
      lastHeartbeat: lastHeartbeat.toLocaleString()
    };
  };

  renderAgentStatus = (agentId) => {
    const status = this.getAgentStatus(agentId);
    if (!status) return null;

    const tooltip = (
      <Tooltip>
        Last heartbeat is {status.minutesDiff} minutes ago ({status.lastHeartbeat})
      </Tooltip>
    );

    return (
      <OverlayTrigger placement="top" overlay={tooltip}>
        <span className="ms-2">
          <BsCircleFill 
            color={status.isOnline ? '#28a745' : '#6c757d'} 
            size={10} 
          />
        </span>
      </OverlayTrigger>
    );
  };

  renderJobInputs = (taskId) => {
    const jobData = this.state.selectedJobs[taskId];
    const job_id = jobData?.job_id;
    const job_content = jobData?.content || {};

    switch (job_id) {
      case -1: // Disconnect ChatGPT
        return null; // No inputs needed for disconnect
      case 101: // Run Script
        const currentTask = this.state.k3tasks.find(t => t._id === taskId);
        const script = this.state.k3scripts.find(s => s._id === currentTask?.script_id);

        console.log("script", script);
        
        if (!script) {
          return (
            <Form.Group className="mb-2">
              <Form.Control
                type="text"
                name="script_params"
                value={job_content.script_params || ''}
                onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
                placeholder="Enter script parameters"
                size="sm"
              />
            </Form.Group>
          );
        }

        // Extract PARAMS variables from script content
        const paramRegex = /K3_[A-Z_]+ = "{{PARAMS\[(\d+)\]}}"/g;
        const params = [];
        let match;
        
        while ((match = paramRegex.exec(script.script_code)) !== null) {
          const paramIndex = parseInt(match[1]);
          const paramName = match[0].split(' = ')[0];
          params.push({ index: paramIndex, name: paramName });
        }

        // Sort params by index
        params.sort((a, b) => a.index - b.index);

        if (params.length === 0) {
          return (
            <Form.Group className="mb-2">
              <Form.Control
                type="text"
                name="script_params"
                value={job_content.script_params || ''}
                onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
                placeholder="Enter script parameters"
                size="sm"
              />
            </Form.Group>
          );
        }

        return (
          <>
            {params.map(param => {
              if (param.name === 'K3_TARGET_AGENT_ID') {
                return (
                  <Form.Group key={param.name} className="mb-2">
                    <Form.Select
                      name={`param_${param.index}`}
                      value={job_content[`param_${param.index}`] || ''}
                      onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
                      size="sm"
                    >
                      <option value="">K3_TARGET_AGENT_ID</option>
                      {this.state.k3agents
                        .filter(agent => agent.main_type === 'AI')
                        .map(agent => (
                          <option key={agent._id} value={agent._id}>
                            {agent.name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                );
              } else if (param.name === 'K3_COMMIT_ID') {
                return (
                  <Form.Group key={param.name} className="mb-2">
                    <Form.Select
                      name={`param_${param.index}`}
                      value={job_content[`param_${param.index}`] || ''}
                      onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
                      size="sm"
                    >
                      <option value="">K3_COMMIT_ID</option>
                      {this.state.commits.map(commit => (
                        <option key={commit._id} value={commit._id}>
                          {commit.title}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                );
              } else {
                return (
                  <Form.Group key={param.name} className="mb-2">
                    <Form.Control
                      type="text"
                      name={`param_${param.index}`}
                      value={job_content[`param_${param.index}`] || ''}
                      onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
                      placeholder={`Enter value for ${param.name}`}
                      size="sm"
                    />
                  </Form.Group>
                );
              }
            })}
          </>
        );
      case 2: // Open conversation
        return (
          <Form.Group className="mb-2">
            <Form.Control
              type="text"
              name="conversation_id"
              value={job_content.conversation_id || ''}
              onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
              placeholder="Enter conversation ID"
              size="sm"
            />
          </Form.Group>
        );
      case 4: // Send Text
        return (
          <Form.Group className="mb-2">
            <Form.Control
              type="text"
              name="text"
              value={job_content.text || ''}
              onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
              placeholder="Enter text message"
              size="sm"
            />
          </Form.Group>
        );
      case 5: // Start New Conversation
        return (
          <>
            <Form.Group className="mb-2">
              <Form.Control
                type="text"
                name="title"
                value={job_content.title || ''}
                onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
                placeholder="Enter chat title"
                size="sm"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Control
                type="text"
                name="text"
                value={job_content.text || ''}
                onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
                placeholder="Enter welcome message"
                size="sm"
              />
            </Form.Group>
          </>
        );
      case 6: // Rename Conversation
        return (
          <>
            <Form.Group className="mb-2">
              <Form.Control
                type="text"
                name="conversation_id"
                value={job_content.conversation_id || ''}
                onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
                placeholder="Enter conversation ID"
                size="sm"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Control
                type="text"
                name="title"
                value={job_content.title || ''}
                onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
                placeholder="Enter new title"
                size="sm"
              />
            </Form.Group>
          </>
        );
      case 102: // Send message to script
        const task = this.state.k3tasks.find(t => t._id === taskId);
        const defaultScriptIdInfo = `${taskId}_${task?.action_count || 0}_${task?.script_id || ''}`;
        
        return (
          <>
            <Form.Group className="mb-2">
              <Form.Control
                type="text"
                name="script_id_info"
                value={job_content.script_id_info || defaultScriptIdInfo}
                onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
                placeholder="Enter script ID info"
                size="sm"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Select
                size="sm"
                name="channel"
                value={job_content.channel || 'command'}
                onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
              >
                <option value="command">Command</option>
                <option value="info">Info</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Select
                size="sm"
                name="type"
                value={job_content.type || 'test'}
                onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
              >
                <option value="test">Test</option>
                <option value="from_server">From Server</option>
                <option value="from_agent">From Agent</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Control
                type="text"
                name="script_message"
                value={job_content.script_message || ''}
                onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
                placeholder="Enter message to send to script"
                size="sm"
              />
            </Form.Group>
          </>
        );
      case 201: // Render Markdown
        if (this.isHelperMarkItDown(this.state.k3tasks.find(t => t._id === taskId)?.target_agent_id)) {
          return (
            <Form.Group className="mb-2">
              <Form.Control
                type="text"
                name="text_input"
                value={job_content.text_input || ''}
                onChange={(e) => this.handleJobContentChange(taskId, job_id, e)}
                placeholder="Enter text"
                size="sm"
              />
            </Form.Group>
          );
        }
        return null;
      default:
        return null;
    }
  };

  handleRunTask = (taskId) => {
    const currentTask = this.state.k3tasks.find(t => t._id === taskId);
    if (!currentTask) return;

    const isHelperMarkItDown = this.isHelperMarkItDown(currentTask.target_agent_id);
    const isPythonRunner = this.isPythonScriptRunner(currentTask.target_agent_id);
    const isChatGPTBot = this.isChatGPTWebBot(currentTask.target_agent_id);
    const jobDataFromState = this.state.selectedJobs[taskId];
    const job_id = jobDataFromState?.job_id;
    const content = jobDataFromState?.content || {};

    // HELPER_MARKITDOWN
    if (isHelperMarkItDown) {
      if (job_id === 201) {
        const job_content = {
          text: content.text_input || '',
          callback_ref: {
            type: 'HUMAN',
            agent_id: currentTask.src_agent_id,
            action_number: currentTask.action_count,
            task_id: taskId,
            job_id: job_id
          }
        };
        const jobData = {
          job_id: 201,
          job_content,
          task_id: taskId,
          agent_id: currentTask.target_agent_id
        };
        this.props.assignAgentJob(jobData);
      }
      return;
    }

    // PYTHON_RUNNER
    if (isPythonRunner) {
      const job_id = jobDataFromState?.job_id || 101; // Default to 101 if not selected
      let job_content = {
        script_id: currentTask.script_id,
        action_signature: uuidv4()
      };
      if (job_id === 101) {
        const script = this.state.k3scripts.find(s => s._id === currentTask.script_id);
        if (script) {
          const paramRegex = /K3_[A-Z_]+ = "{{PARAMS\[(\d+)\]}}"/g;
          const params = [];
          let match;
          while ((match = paramRegex.exec(script.script_code)) !== null) {
            const paramIndex = parseInt(match[1]);
            const paramName = match[0].split(' = ')[0];
            params.push({ index: paramIndex, name: paramName });
          }
          params.sort((a, b) => a.index - b.index);
          const targetAgentParam = params.find(p => p.name === 'K3_TARGET_AGENT_ID');
          const commitIdParam = params.find(p => p.name === 'K3_COMMIT_ID');
          const targetAgentId = targetAgentParam ? jobDataFromState?.content[`param_${targetAgentParam.index}`] : '';
          const commitId = commitIdParam ? jobDataFromState?.content[`param_${commitIdParam.index}`] : '';
          const script_params = `${targetAgentId}@${commitId}`;
          job_content = {
            ...job_content,
            script_params: script_params
          };
        } else {
          job_content = {
            ...job_content,
            script_params: jobDataFromState?.content?.script_params || ''
          };
        }
      } else if (job_id === 102) {
        const defaultScriptIdInfo = `${taskId}_${currentTask?.action_count || 0}_${currentTask?.script_id || ''}`;
        var scriptMessageObject = {
          channel: jobDataFromState?.content?.channel || 'command',
          type: jobDataFromState?.content?.type || 'test',
          payload: {
            text: jobDataFromState?.content?.script_message || '',
            timestamp: new Date().getTime()
          }
        };
        job_content = {
          ...job_content,
          script_id_info: jobDataFromState?.content?.script_id_info || defaultScriptIdInfo,
          script_message: JSON.stringify(scriptMessageObject)
        };
      }
      const jobData = {
        job_id: job_id,
        job_content: job_content,
        task_id: taskId,
        agent_id: currentTask.target_agent_id
      };
      this.props.assignAgentJob(jobData);
      return;
    }

    // CHATGPT_BOT
    if (isChatGPTBot) {
      if (!job_id) {
        console.log('No job selected for task');
        return;
      }
      let job_content = {};
      switch (job_id) {
        case -1:
          job_content = {};
          break;
        case 2:
          job_content = { conversation_id: content.conversation_id };
          break;
        case 4:
          job_content = { text: content.text };
          break;
        case 5:
          job_content = { title: content.title, text: content.text };
          break;
        case 6:
          job_content = { conversation_id: content.conversation_id, title: content.title };
          break;
        default:
          job_content = {};
      }
      const jobData = {
        task_id: taskId,
        agent_id: currentTask.target_agent_id,
        job_id: job_id,
        job_content: job_content
      };
      this.props.assignAgentJob(jobData);
      return;
    }
    // If not any of the above, do nothing
    return;
  };

  handleShowLogs = async (taskId) => {
    this.setState({
      selectedTaskId: taskId,
      showLogModal: true
    });
    
    // Fetch logs for the selected task
    await this.props.getK3TaskLogs({ task_id: taskId });
  };

  handleCloseLogModal = () => {
    this.setState({
      showLogModal: false,
      selectedTaskId: null,
      taskLogs: []
    });
  };

  handleFilterChange = (e) => {
    const { name, value } = e.target;
    this.setState(prevState => ({
      filters: {
        ...prevState.filters,
        [name]: value
      }
    }));
  };

  getFilteredTasks = () => {
    const { filters } = this.state;
    const { k3tasks } = this.props;

    return k3tasks.filter(task => {
      if (filters.type && task.type !== filters.type) return false;
      if (filters.status && task.status !== filters.status) return false;
      if (filters.priority && task.priority !== filters.priority) return false;
      return true;
    });
  };

  handleCreateTask = async (taskData) => {
    try {
      await this.props.setK3Tasks([taskData]);
      await this.props.getK3Tasks();
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  render() {
    const { k3agents, k3tasks, k3scripts, toast, commits } = this.state;
    const { formData, loading, selectedTask, showTaskView, showCreateModal, selectedJobs, showLogModal, selectedTaskId } = this.state;
    const task = this.state.k3tasks.find(t => t._id === selectedTaskId);
    const filteredTasks = this.getFilteredTasks();

    return (
      <div className="py-4">
        <ToastContainer position="top-end" className="p-3">
          <Toast 
            show={toast.show} 
            onClose={() => this.setState({ toast: { ...toast, show: false } })}
            bg={toast.type}
            delay={3000}
            autohide
          >
            <Toast.Header closeButton={false}>
              <strong className="me-auto">
                {toast.type === 'success' ? 'Success' : 'Error'}
              </strong>
            </Toast.Header>
            <Toast.Body className={toast.type === 'success' ? 'text-white' : ''}>
              {toast.message}
            </Toast.Body>
          </Toast>
        </ToastContainer>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Task List</h4>
          <Button variant="primary" onClick={this.handleShowCreateModal}>
            <BsPlus className="me-1" /> Create New Task
          </Button>
        </div>

        <Card>
          <Card.Body>
            <Row className="mb-4">
              <Col md={4}>
                <Form.Select
                  name="type"
                  value={this.state.filters.type}
                  onChange={this.handleFilterChange}
                  className="w-auto"
                >
                  <option value="">All Types</option>
                  <option value="AI CHAT">AI CHAT</option>
                  <option value="AI HELPER">AI HELPER</option>
                  <option value="SCRIPT RUNNER">SCRIPT RUNNER</option>
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Select
                  name="status"
                  value={this.state.filters.status}
                  onChange={this.handleFilterChange}
                  className="w-auto"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Select
                  name="priority"
                  value={this.state.filters.priority}
                  onChange={this.handleFilterChange}
                  className="w-auto"
                >
                  <option value="">All Priorities</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </Form.Select>
              </Col>
            </Row>

            <Table striped hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Source Agent</th>
                  <th>Target Agent</th>
                  {(k3tasks.some(task => this.isChatGPTWebBot(task.target_agent_id)) || 
                    k3tasks.some(task => this.isPythonScriptRunner(task.target_agent_id))) && <th>Job</th>}
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map(task => {
                  const isChatGPTBot = this.isChatGPTWebBot(task.target_agent_id);
                  const isPythonRunner = this.isPythonScriptRunner(task.target_agent_id);
                  const isHelperMarkItDown = this.isHelperMarkItDown(task.target_agent_id);
                  const currentJobData = selectedJobs[task._id];
                  const job_id = currentJobData?.job_id || '';

                  return (
                    <tr key={task._id}>
                      <td>{task.name}</td>
                      <td>
                        <Badge bg="info">{task.type}</Badge>
                      </td>
                      <td>{this.getStatusBadge(task.status)}</td>
                      <td>{this.getPriorityBadge(task.priority)}</td>
                      <td>
                        {this.getAgentName(task.src_agent_id)}
                        {this.renderAgentStatus(task.src_agent_id)}
                      </td>
                      <td>
                        {this.getAgentName(task.target_agent_id)}
                        {this.renderAgentStatus(task.target_agent_id)}
                      </td>
                      <td>
                        {isChatGPTBot && (
                          <>
                            <Form.Select
                              size="sm"
                              value={job_id}
                              onChange={(e) => this.handleJobChange(task._id, e)}
                            >
                              <option value="">Select Job</option>
                              <option value="-1">[-1] Disconnect ChatGPT</option>
                              <option value="1">[1] Connect to ChatGPT session</option>
                              <option value="2">[2] Open conversation</option>
                              <option value="3">[3] Get ChatGPT Conversation</option>
                              <option value="4">[4] Send Text</option>
                              <option value="5">[5] Start New Conversation</option>
                              <option value="6">[6] Rename Conversation</option>
                            </Form.Select>
                            {job_id && this.renderJobInputs(task._id)}
                          </>
                        )}
                        {isPythonRunner && (
                          <>
                            <Form.Select
                              size="sm"
                              value={job_id}
                              onChange={(e) => this.handleJobChange(task._id, e)}
                            >
                              <option value="">Select Job</option>
                              <option value="101">[101] Run Script</option>
                              <option value="102">[102] Send message to script</option>
                            </Form.Select>
                            {job_id && this.renderJobInputs(task._id)}
                          </>
                        )}
                        {isHelperMarkItDown && (
                          <>
                            <Form.Select
                              size="sm"
                              value={job_id}
                              onChange={(e) => this.handleJobChange(task._id, e)}
                            >
                              <option value="">Select Job</option>
                              <option value="201">[201] Render Markdown</option>
                            </Form.Select>
                            {job_id && this.renderJobInputs(task._id)}
                          </>
                        )}
                      </td>
                      <td>
                        <div>{new Date(task.createdAt).toLocaleDateString()}</div>
                        <small className="text-muted">{new Date(task.createdAt).toLocaleTimeString()}</small>
                      </td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => this.handleRunTask(task._id)}
                          className="me-2"
                        >
                          Run
                        </Button>
                        <Button
                          variant="info"
                          size="sm"
                          onClick={() => this.handleShowLogs(task._id)}
                          className="me-2"
                        >
                          <BsList />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => this.handleEditTask(task)}
                        >
                          <BsPencil />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <K3TaskCreateModal
          show={showCreateModal}
          onHide={this.handleCloseCreateModal}
          k3agents={k3agents}
          k3scripts={k3scripts}
          onCreateTask={this.handleCreateTask}
        />

        {showTaskView && (
          <K3TaskView
            show={showTaskView}
            onHide={this.handleCloseTaskView}
            selectedTask={selectedTask}
          />
        )}

        {showLogModal && task && (
          <K3TaskLogModal
            show={showLogModal}
            onHide={this.handleCloseLogModal}
            task={task}
            logsData={this.props.k3taskLogs}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  k3agents: state.k3agentReducer.k3agents,
  k3tasks: state.k3taskReducer.k3tasks,
  k3taskLogs: state.k3taskReducer.logs,
  k3scripts: state.k3scriptReducer.scripts,
  commits: state.commitReducer.commits
});

export default connect(mapStateToProps, { 
  getK3Agents, 
  setK3Tasks, 
  getK3Tasks,
  assignAgentJob,
  getK3TaskLogs,
  getK3Scripts,
  getCommits
})(K3TaskPlayground); 